import * as React from "react";
import {
  AlertCircle,
  Download,
  Heart,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Search,
  Users,
  UserPlus2,
  X,
} from "lucide-react";
import {
  Button,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  type StatusPillStatus,
} from "@eduviaos/ui-kit";
import type { Parent, ParentListResponse, ParentRelationship } from "@eduviaos/shared";
import { useParentsList, PARENT_RELATIONSHIPS } from "@/hooks/useParents";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import { api } from "@/lib/api";
import { ExportCsvErrorBanner } from "@/components/ExportCsvErrorBanner";
import { useCsvExport } from "@/hooks/useCsvExport";
import { ParentEditorSheet } from "@/components/people/ParentEditorSheet";
import { ParentStudentsSheet } from "@/components/people/ParentStudentsSheet";

/**
 * Phase J3 + J3.b — Parents/Guardians roster inside `<PeoplePage>`.
 *
 * Read + write surface. The BE has no soft-delete or status-transition
 * endpoint for parents — the household lifecycle is managed implicitly
 * by detaching `parent_id` on the linked students, so there's
 * intentionally no Remove action in the row menu. J3.b adds:
 *   - "Add parent" toolbar button → `<ParentEditorSheet>` in create mode
 *   - Row "Edit" action            → `<ParentEditorSheet>` in edit mode
 *
 * Write affordances are gated on `canManage` (caller passes
 * `manage_students` permission — the BE shares the same permission
 * for student + parent PII).
 *
 * Filters:
 *   - Relationship (server-side, BE `?relationship=`)
 *   - q-search (client-side over loaded page — name / email / phone /
 *     city; BE `/api/parents/` doesn't carry `?q=` yet)
 *
 * Pagination: 50/page default with offset/limit; footer shows the
 * loaded slice + total.
 *
 * Status pill semantics:
 *   - Phone-verified → `active` (emerald)
 *   - Phone-NOT-verified → `invited` (amber slate)
 *   The "verified" signal matters more than the relationship for the
 *   admissions team — it's the predicate on whether OTP-based parent
 *   portal flows will land.
 *
 * Iron Rule #16: uses the `<DataTable>` primitive — sticky thead +
 * `<StatusPill>` for verification status + action column on the right
 * (placeholder hyphen until J3.b row actions land).
 */

// Phase E1.f — default rows-per-page; user can override via the
// shared `<DataTablePagination>` selector (persisted per-user via
// localStorage scope "parents").
const DEFAULT_PAGE_SIZE = 10;

const RELATIONSHIP_FILTER_OPTIONS: Array<{
  value: "__any__" | ParentRelationship;
  label: string;
}> = [
  { value: "__any__", label: "Any relationship" },
  ...PARENT_RELATIONSHIPS.map((r) => ({ value: r.value as ParentRelationship, label: r.label })),
];

function verificationPillVariant(parent: Parent): StatusPillStatus {
  if (parent.is_phone_verified) return "active";
  return "invited";
}

function verificationPillLabel(parent: Parent): string {
  if (parent.is_phone_verified && parent.is_email_verified) return "Verified";
  if (parent.is_phone_verified) return "Phone verified";
  return "Unverified";
}

function relationshipLabel(rel: string): string {
  const match = PARENT_RELATIONSHIPS.find((r) => r.value === rel);
  return match ? match.label : rel.charAt(0).toUpperCase() + rel.slice(1);
}

function locationLabel(parent: Parent): string | null {
  const parts = [parent.city, parent.state].filter((p): p is string => Boolean(p));
  return parts.length === 0 ? null : parts.join(", ");
}

export interface ParentsTabProps {
  /**
   * Set to true when the active user has `manage_students`. When true,
   * the toolbar shows "Add parent" and each row exposes an Edit action.
   * When false the action column collapses to a hyphen.
   */
  canManage?: boolean;
  /**
   * Phase J4.c — Canonical tenant slug used in the CSV export filename
   * (`parents-<tenant_slug>-<YYYY-MM-DD>.csv`). Defaults to `null`
   * which renders as the generic `parents-export-<date>.csv` fallback.
   */
  tenantSlug?: string | null;
}

type DialogState =
  | { kind: "none" }
  | { kind: "create" }
  | { kind: "edit"; parent: Parent }
  | { kind: "view-students"; parent: Parent };

export function ParentsTab({ canManage = false, tenantSlug = null }: ParentsTabProps = {}) {
  const [relationshipFilter, setRelationshipFilter] = React.useState<
    "__any__" | ParentRelationship
  >("__any__");
  const [qInput, setQInput] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "parents",
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });
  const [dialog, setDialog] = React.useState<DialogState>({ kind: "none" });

  const filters = React.useMemo(
    () => ({
      relationship: relationshipFilter === "__any__" ? undefined : relationshipFilter,
      limit: pageSize,
      offset: page * pageSize,
    }),
    [relationshipFilter, page, pageSize],
  );

  const query = useParentsList(filters);
  const { data, isLoading, isFetching, isError, error, refetch } = query;

  const total = data?.total ?? 0;
  const allItems = React.useMemo(() => data?.items ?? [], [data]);

  const filteredItems = React.useMemo(() => {
    const needle = qInput.trim().toLowerCase();
    if (!needle) return allItems;
    return allItems.filter((p) => {
      const haystack = [
        p.name,
        p.email ?? "",
        p.phone,
        p.alt_phone ?? "",
        p.city ?? "",
        p.state ?? "",
        p.occupation ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [allItems, qInput]);

  const hasFiltersApplied = relationshipFilter !== "__any__" || qInput !== "";

  const clearFilters = React.useCallback(() => {
    setRelationshipFilter("__any__");
    setQInput("");
    setPage(0);
  }, []);

  // Phase J4.c — CSV export of the filtered parents roster.
  // Mirrors the J4.a Staff + J4.b Students implementations: iteratively
  // fetches server-side-filtered pages in 200-row chunks honoring the
  // live `relationship` filter, then applies the client-side `q`
  // filter to the collected superset (the BE `/api/parents/` v1
  // surface doesn't yet carry `?q=`). Output filename:
  // `parents-<tenant_slug>-<YYYY-MM-DD>.csv`. Errors surface through a
  // dedicated dismissable inline banner above the existing list error.
  // Phase D2.f — CSV export wiring delegated to the shared
  // `useCsvExport` primitive. See StudentsTab for the full rationale;
  // ParentsTab follows the same migration pattern verbatim.
  const csvExport = useCsvExport({
    filenamePrefix: "parents",
    tenantSlug,
    disabled: total === 0 && !isLoading,
    formatError: (err) =>
      err instanceof Error && err.message
        ? `Couldn't export the parents roster as CSV: ${err.message}`
        : "Couldn't export the parents roster as CSV.",
    buildRows: async () => {
      const collected: Parent[] = [];
      const CHUNK = 200;
      const HARD_CAP = 50_000;
      let offset = 0;
      let expectedTotal: number | null = null;
      while (collected.length < HARD_CAP) {
        const search = new URLSearchParams();
        if (relationshipFilter !== "__any__") search.set("relationship", relationshipFilter);
        search.set("limit", String(CHUNK));
        search.set("offset", String(offset));
        const page = await api.get<ParentListResponse>(`/api/parents/?${search.toString()}`);
        collected.push(...page.items);
        if (expectedTotal === null) expectedTotal = page.total;
        offset += page.items.length;
        if (page.items.length === 0) break;
        if (page.items.length < CHUNK) break;
        if (expectedTotal !== null && collected.length >= expectedTotal) break;
      }

      const needle = qInput.trim().toLowerCase();
      const rows = needle
        ? collected.filter((p) => {
            const haystack = [
              p.name,
              p.email ?? "",
              p.phone,
              p.alt_phone ?? "",
              p.city ?? "",
              p.state ?? "",
              p.occupation ?? "",
            ]
              .join(" ")
              .toLowerCase();
            return haystack.includes(needle);
          })
        : collected;

      return {
        columns: [
          "Name",
          "Relationship",
          "Phone",
          "Alt phone",
          "Email",
          "Address line 1",
          "Address line 2",
          "City",
          "State",
          "Pincode",
          "Occupation",
          "Phone verified",
          "Email verified",
          "Consent given at",
          "Created at",
        ],
        rows: rows.map((p) => [
          p.name,
          relationshipLabel(p.relationship),
          p.phone,
          p.alt_phone,
          p.email,
          p.address_line1,
          p.address_line2,
          p.city,
          p.state,
          p.pincode,
          p.occupation,
          p.is_phone_verified ? "yes" : "no",
          p.is_email_verified ? "yes" : "no",
          p.consent_given_at,
          p.created_at,
        ]),
      };
    },
  });

  const exportDisabled = csvExport.isExporting || (total === 0 && !isLoading);

  return (
    <div data-testid="people-parents-tab">
      <ExportCsvErrorBanner
        variant="inline"
        testid="parents-export-error"
        error={csvExport.exportError}
        onDismiss={csvExport.dismissError}
        className="mb-4"
      />

      {isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive mb-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">
            Couldn&apos;t load parents
            {error instanceof Error && error.message ? `: ${error.message}` : "."}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-0.5 text-xs"
            onClick={() => refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      <DataTable>
        <DataTableToolbar>
          <div className="relative w-full sm:w-72">
            <Search
              aria-hidden="true"
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            />
            <Input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Search name, email, phone, city…"
              className="pl-8"
              aria-label="Search parents"
            />
          </div>
          <Select
            value={relationshipFilter}
            onValueChange={(v) => {
              setRelationshipFilter(v as typeof relationshipFilter);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-9 w-44" aria-label="Relationship filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RELATIONSHIP_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasFiltersApplied ? (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              <X aria-hidden="true" className="size-3.5" />
              Clear
            </Button>
          ) : null}
          <DataTableToolbarSpacer />
          <Button
            variant="ghost"
            size="sm"
            onClick={csvExport.handleExport}
            disabled={exportDisabled}
            aria-label="Download parents roster as CSV"
            data-testid="parents-export-csv"
          >
            <Download
              aria-hidden="true"
              className={cn("size-4", csvExport.isExporting && "animate-pulse")}
            />
            {csvExport.isExporting ? "Exporting…" : "Download CSV"}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw aria-hidden="true" className={cn("size-4", isFetching && "animate-spin")} />
            Refresh
          </Button>
          <span className="text-muted-foreground text-xs tabular-nums">
            {total.toLocaleString()} total
          </span>
          {canManage ? (
            <Button
              size="sm"
              onClick={() => setDialog({ kind: "create" })}
              data-testid="parents-add-button"
            >
              <UserPlus2 aria-hidden="true" className="size-4" />
              Add parent
            </Button>
          ) : null}
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[22%]">Name</TableHead>
                <TableHead className="w-[12%]">Relationship</TableHead>
                <TableHead className="w-[20%]">Phone</TableHead>
                <TableHead className="w-[20%]">Email</TableHead>
                <TableHead className="w-[14%]">Location</TableHead>
                <TableHead className="w-[12%]">Status</TableHead>
                <TableHead className="w-[6%] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={5} columns={7} />
            ) : (
              <TableBody>
                {filteredItems.map((p) => {
                  const location = locationLabel(p);
                  return (
                    <TableRow key={p.id} data-testid={`parent-row-${p.id}`}>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {relationshipLabel(p.relationship)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs tabular-nums">
                        <div className="truncate">{p.phone}</div>
                        {p.alt_phone ? <div className="truncate">{p.alt_phone}</div> : null}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        <div className="truncate">{p.email ?? "—"}</div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {location ?? "—"}
                      </TableCell>
                      <TableCell>
                        <StatusPill
                          status={verificationPillVariant(p)}
                          label={verificationPillLabel(p)}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Actions for ${p.name}`}
                              data-testid={`parent-row-actions-${p.id}`}
                              className="size-7"
                            >
                              <MoreHorizontal aria-hidden="true" className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem
                              onSelect={() => setDialog({ kind: "view-students", parent: p })}
                              data-testid={`parent-action-view-students-${p.id}`}
                            >
                              <Users aria-hidden="true" className="size-4" />
                              View linked students
                            </DropdownMenuItem>
                            {canManage ? (
                              <DropdownMenuItem
                                onSelect={() => setDialog({ kind: "edit", parent: p })}
                                data-testid={`parent-action-edit-${p.id}`}
                              >
                                <Pencil aria-hidden="true" className="size-4" />
                                Edit
                              </DropdownMenuItem>
                            ) : null}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && filteredItems.length === 0 ? (
          <DataTableEmpty
            icon={<Heart aria-hidden="true" />}
            title={hasFiltersApplied ? "No matches" : "No parents yet"}
            description={
              hasFiltersApplied
                ? "Adjust filters or clear them to see every parent."
                : "Parent records show up here once you create them. The Students importer (and the Add parent flow) populate this roster."
            }
            action={
              hasFiltersApplied ? (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  <X aria-hidden="true" className="size-3.5" />
                  Clear filters
                </Button>
              ) : canManage ? (
                <Button size="sm" onClick={() => setDialog({ kind: "create" })}>
                  <UserPlus2 aria-hidden="true" className="size-4" />
                  Add parent
                </Button>
              ) : null
            }
          />
        ) : null}

        {!isLoading && filteredItems.length > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              Showing {filteredItems.length.toLocaleString()} of {total.toLocaleString()}
            </span>
            <DataTablePagination
              className="ml-auto"
              page={page + 1}
              pageSize={pageSize}
              totalRows={total}
              isFetching={isFetching}
              onPageChange={(next) => setPage(Math.max(0, next - 1))}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(0);
              }}
              testIdPrefix="parents-pagination"
            />
          </DataTableFooter>
        ) : null}
      </DataTable>

      {canManage ? (
        <ParentEditorSheet
          open={dialog.kind === "create" || dialog.kind === "edit"}
          onOpenChange={(next) => {
            if (!next) setDialog({ kind: "none" });
          }}
          existing={dialog.kind === "edit" ? dialog.parent : null}
          onSaved={() => setDialog({ kind: "none" })}
        />
      ) : null}

      <ParentStudentsSheet
        open={dialog.kind === "view-students"}
        onOpenChange={(next) => {
          if (!next) setDialog({ kind: "none" });
        }}
        parent={dialog.kind === "view-students" ? dialog.parent : null}
      />
    </div>
  );
}
